/**
 * @author Brendan Magee
 * @description Centralized service that handles all notification rules
 */

function notificationService(
  $http,
  $q,
  $translate,
  taskTypesService,
  timezoneService,
  schemaService,
  groupsService,
  Publication,
  siteService,
  platformService,
  companyService
) {
  const baseMethodUrl = 'api/notifications';

  const notificationContainer = {
    id: null,
    recipient: {
      type: '',
      id: ''
    },
    trigger: {
      type: null,
      pitch: {
        path: null,
        website: null,
        pitchStatus: ['watching', 'saving'],
        breakingOnly: true
      },
      pitchStatus: [],
      creators: [],
      primaryWebsites: [],
      sections: [],
      exclude: [],
      deadline: null,
      taskType: null,
      createdBy: null,
      contentType: null,
      priority: 1,
      story: {
        creators: [],
        primaryWebsites: [],
        sections: []
      },
      assignee: {
        type: 'user',
        id: null
      }
    },
    target: {
      type: 'slack',
      timezone: timezoneService.userTimezone,
      authToken: null,
      integrationID: null,
      channel: null,
      username: null,
      address: null,
      htmlEnabled: true,
      jsonPayload: false,
      webhookURL: null
    }
  };

  const service = {};

  service.triggerTypes = [
    {
      id: 'story_publish',
      name: $translate.instant('STORY_PUBLISH'),
      blocks: [
        'pitch',
        'creators',
        'sections',
        'contentType',
        'primaryWebsites'
      ]
    },
    {
      id: 'story_deadline',
      name: $translate.instant('STORY_DEADLINE'),
      blocks: ['creators', 'deadline', 'sections', 'pitch', 'primaryWebsites'],
      defaults: ['deadline'],
      required: ['deadline']
    },
    {
      id: 'task_create',
      name: $translate.instant('TASK_CREATE'),
      blocks: ['taskType', 'assignee', 'priority', 'deadline', 'story']
    },
    {
      id: 'task_assign',
      name: $translate.instant('TASK_ASSIGN'),
      blocks: [
        'taskType',
        'createdBy',
        'assignee',
        'priority',
        'deadline',
        'story'
      ]
    },
    {
      id: 'task_claim',
      name: $translate.instant('TASK_CLAIM'),
      blocks: [
        'taskType',
        'createdBy',
        'assignee',
        'priority',
        'deadline',
        'story'
      ]
    },
    {
      id: 'task_deadline',
      name: $translate.instant('TASK_DEADLINE'),
      blocks: ['taskType', 'createdBy', 'assignee', 'priority', 'deadline'],
      required: ['deadline'],
      defaults: ['deadline']
    },
    {
      id: 'task_complete',
      name: $translate.instant('TASK_COMPLETE'),
      blocks: ['taskType', 'createdBy', 'assignee', 'story']
    },
    {
      id: 'pitch_create',
      name: $translate.instant('PLATFORM_PITCH_CREATE'),
      blocks: ['pitch', 'story'],
      required: ['pitch'],
      defaults: ['pitch']
    },
    {
      id: 'publication_pitch_create',
      name: $translate.instant('PUBLICATION_PITCH_CREATE'),
      blocks: ['story', 'publicationId'],
      required: ['publicationId'],
      defaults: ['publicationId']
    },
    {
      id: 'publication_pitch_update',
      name: $translate.instant('PUBLICATION_PITCH_UPDATE'),
      blocks: ['publicationId', 'story', 'pitchStatus'],
      required: ['publicationId'],
      defaults: ['publicationId']
    },
    {
      id: 'edition_finalize',
      name: $translate.instant('FINALIZE_EDITION'),
      blocks: ['publicationId'],
      required: ['publicationId'],
      defaults: ['publicationId']
    }
  ];

  service.targetTypes = [
    {
      id: 'slack',
      name: 'Slack',
      icon: 'fa-slack',
      target(target) {
        return target.username ? target.username : target.channel;
      },
      fields: ['timezone', 'integrationID', 'channel', 'username'],
      required: ['timezone', 'channel', 'username', 'integrationID']
    },
    {
      id: 'teams',
      name: 'Teams',
      target: (target) => target.displayName,
      fields: [
        'timezone',
        'integrationID',
        'channelId',
        'userId',
        'displayName'
      ],
      required: [
        'timezone',
        'integrationID',
        'channelId',
        'userId',
        'displayName'
      ]
    },
    {
      id: 'email',
      name: $translate.instant('EMAIL'),
      icon: 'fa-envelope',
      target(target) {
        return target.address;
      },
      fields: ['timezone', 'address', 'htmlEnabled'],
      required: ['address']
    },
    {
      id: 'webhook',
      name: $translate.instant('WEBHOOK'),
      icon: 'fa-code-fork',
      target(target) {
        return target.endpoint;
      },
      fields: ['endpoint', 'authToken', 'secretsEncrypted', 'jsonPayload'],
      required: ['endpoint', 'authToken']
    }
  ];

  service.triggerParameterTypes = [
    {
      id: 'creators',
      name: $translate.instant('AUTHORS'),
      value(authors) {
        return Array.isArray(authors) ? authors.join(', ') : authors;
      }
    },
    {
      id: 'createdBy',
      name: $translate.instant('CREATED_BY'),
      value(username) {
        return username;
      }
    },
    {
      id: 'primaryWebsites',
      name: $translate.instant('MULTISITE.PRIMARY_WEBSITES'),
      value(websiteData) {
        return websiteData.join(', ');
      }
    },
    {
      id: 'sections',
      name: $translate.instant('SECTIONS'),
      value(sectionData) {
        const sections = sectionData.map((section) => {
          const { path, inheritance } = section;
          const andChildren = $translate.instant('AND_CHILDREN');

          const returnVal = `"${
            path || $translate.instant('MISSING_PLAN_PATH_ERROR')
          }" ${inheritance ? andChildren : ''}`;
          return returnVal.trim();
        });

        return sections.join(', ');
      }
    },
    {
      id: 'pitch',
      name: $translate.instant('PITCH'),
      value(pitch) {
        const destination = `"${pitch.path}"`;
        const website = service.websites.find(
          (site) => site.id === pitch.website
        ) || { name: '' };
        const breaking = pitch.breakingOnly
          ? `(${$translate.instant('BREAKING_NEWS_ONLY')})`
          : '';

        const description = `${website.name} ${destination} ${breaking}`;

        return description.trim();
      }
    },
    {
      id: 'deadline',
      name: $translate.instant('DEADLINE'),
      value(numberMinutes) {
        const minutes = $translate.instant('MINUTES');
        return `${numberMinutes} ${minutes}`;
      }
    },
    {
      id: 'priority',
      name: $translate.instant('PRIORITY'),
      value(priorityId) {
        return service.getDisplayName(priorityId, 'priorities');
      }
    },
    {
      id: 'taskType',
      name: $translate.instant('TASK_TYPE'),
      value(taskId) {
        return service.getDisplayName(taskId, 'taskTypes');
      }
    },
    {
      id: 'contentType',
      name: $translate.instant('CONTENT_TYPE'),
      value(typeId) {
        return service.getDisplayName(typeId, 'contentTypes');
      }
    },
    {
      id: 'assignee',
      name: $translate.instant('TASK_ASSIGNEE'),
      value(assignee) {
        const isUser = assignee.type === 'user';
        const type = isUser
          ? $translate.instant('USER')
          : $translate.instant('GROUP');
        const name = isUser
          ? assignee.id
          : service.getDisplayName(assignee.id, 'groups', 'displayName');

        return `${type}: ${name}`;
      }
    },
    {
      id: 'pitchStatus',
      name: $translate.instant('PITCH_STATUS'),
      value(statuses) {
        return statuses;
      }
    },
    {
      id: 'publicationId',
      name: $translate.instant('PUBLICATION'),
      value(publicationId) {
        return service.getDisplayName(publicationId, 'publications');
      }
    }
  ];

  service.isTargetFieldRequired = (notification, fieldName) => {
    // for now all fields are required to be passed to server
    // if this changes use 'required' instead of 'fields' in the last line of this function
    if (fieldName === 'type') {
      return true;
    }

    if (!notification || !notification.target || !notification.target.type) {
      return false;
    }

    const target = service.targetTypes.find(
      (targ) => targ.id === notification.target.type
    );
    const fields = target && target.fields ? target.fields : [];

    return fields.indexOf(fieldName) >= 0;
  };

  const initRequests = (deferred) => {
    $q.all([
      taskTypesService.getTaskTypes(),
      groupsService.getGroups(),
      schemaService.getContentTypes(),
      schemaService.getTaskPriorities(),
      schemaService.getPitchStatuses('platform'),
      schemaService.getPitchStatuses('publication'),
      Publication.getPublicationsList({ size: 5000 }),
      siteService.getWebsites(),
      platformService.getAll(),
      companyService.get()
    ]).then(
      (data) => {
        service.taskTypes = data[0];
        service.groups = data[1];
        service.contentTypes = data[2];
        service.priorities = data[3];
        service.pitchStatuses = data[4];
        service.publicationPitchStatuses = data[5];
        service.publications = data[6].publications;
        service.publicationSections = service.publications.reduce(
          (acc, pub) => {
            const sections = pub.sections ? [...pub.sections] : [];
            return acc.concat(sections);
          },
          []
        );

        service.allStatuses = [
          ...service.pitchStatuses,
          ...service.publicationPitchStatuses
        ].reduce((acc, status) => {
          if (!acc.some((el) => el.id === status.id)) {
            acc.push(status);
          }
          return acc;
        }, []);

        service.websites = data[7].map((website) => ({
          id: website._id,
          name: website.display_name
        }));

        service.platforms = data[8].map((platform) => ({
          name: platform.title,
          title: `${platform.title} (${platform.path})`,
          path: platform.path,
          website: platform.website
        }));

        service.siteServiceHierarchies = data[9].siteServiceHierarchies;

        deferred.resolve();
      },
      (error) => {
        deferred.reject(error);
      }
    );
  };

  service.init = () => {
    const d = $q.defer();
    service.initialized = d.promise;

    initRequests(d);

    return d.promise;
  };

  service.getNewNotification = (recipientType, recipientID) => {
    const notification = angular.copy(notificationContainer);
    notification.recipient.type = recipientType;
    notification.recipient.id = recipientID;
    return notification;
  };

  service.cloneNotification = (existingNotification, user) => {
    const notification = angular.copy(existingNotification);
    notification.recipient.id = user.username;
    notification.id = null;
    return notification;
  };

  service.getDisplayName = (id, list, property = 'name') => {
    if (service[list] && service[list].length) {
      const display = service[list].find((item) => item.id === id);

      return display
        ? display[property]
        : `${$translate.instant('UNKNOWN')}: ${id}`;
    }

    return id;
  };

  service.translatePitchStatuses = (statuses) => {
    if (service.allStatuses.length && statuses.length) {
      const translated = service.allStatuses.reduce((acc, status) => {
        if (statuses.indexOf(status.id) >= 0) {
          acc.push(status.name);
        }
        return acc;
      }, []);

      return translated.join(', ');
    }

    return Array.isArray(statuses) ? statuses.join(', ') : statuses;
  };

  service.buildTargetDisplay = (target) => {
    const targetType = service.targetTypes.find(
      (item) => item.id === target.type
    );

    return targetType.target(target);
  };

  service.buildTriggerDisplay = (trigger) => {
    const deepProps = [];

    const result = Object.keys(trigger).reduce((acc, prop) => {
      const value = trigger[prop];
      const parameter = service.triggerParameterTypes.find(
        (type) => type.id === prop
      );
      const emptyArray = Array.isArray(value) && !value.length;

      if ((value || value === 0) && !emptyArray) {
        switch (prop) {
          case 'type': {
            break;
          }

          case 'story': {
            deepProps.push(...service.buildTriggerDisplay(value));
            break;
          }

          case 'pitch': {
            acc.push({
              name: parameter.name,
              description: parameter.value(value)
            });
            deepProps.push(...service.buildTriggerDisplay(value));
            break;
          }

          case 'pitchStatus': {
            const val = parameter.value(value);
            const translated = service.translatePitchStatuses(val);
            acc.push({ name: parameter.name, description: translated });
            break;
          }

          case 'publicationId': {
            acc.push({
              name: parameter.name,
              description: parameter.value(value)
            });
            if (trigger.sectionId) {
              const section = service.publicationSections.find(
                (sec) =>
                  sec.publicationId === value && sec.id === trigger.sectionId
              );
              if (section) {
                acc.push({
                  name: $translate.instant('PUBLICATION_SECTIONS.TITLE_S'),
                  description: section.name
                });
              }
            }
            break;
          }

          default: {
            if (parameter) {
              acc.push({
                name: parameter.name,
                description: parameter.value(value)
              });
            }
            break;
          }
        }
      }

      return acc;
    }, []);

    return [...result, ...deepProps];
  };

  service.generateCardDisplay = (notification) => {
    const { trigger, target } = notification;

    const cardData = {
      type: service.getDisplayName(trigger.type, 'triggerTypes'),
      triggerParams: service.buildTriggerDisplay(trigger),
      target: service.buildTargetDisplay(target)
    };

    return cardData;
  };

  service.getEasyNotifications = (recipientID) =>
    $http
      .get(baseMethodUrl, {
        params: {
          recipientID,
          easyNotifications: true,
          recipientType: 'user',
          page: 0,
          size: 10
        },
        cache: false
      })
      .then((response) => response.data.results);

  service.getAll = (recipientType, recipientID, page, limit) =>
    $http
      .get(baseMethodUrl, {
        params: {
          recipientID,
          recipientType,
          page: page || 0,
          size: limit || 20
        },
        cache: false
      })
      .then((response) => {
        const { results } = response.data;
        results.forEach((result) => {
          const notification = result;
          notification.cardData = service.generateCardDisplay(result);

          return notification;
        });

        return results;
      });

  service.get = (id) =>
    $http.get(`${baseMethodUrl}/${id}`).then((response) => {
      const notification = response.data;
      notification.cardData = service.generateCardDisplay(response.data);

      return notification;
    });

  service.delete = (notification) =>
    $http.delete(`${baseMethodUrl}/${notification.id}`).then((response) => {
      const deletedNotification = response.data;

      return deletedNotification;
    });

  service.create = (notification) =>
    $http.post(baseMethodUrl, notification).then((response) => {
      const newNotification = response.data;

      return newNotification;
    });

  service.update = (notification) =>
    $http
      .put(`${baseMethodUrl}/${notification.id}`, notification)
      .then((response) => {
        const updatedNotification = response.data;

        return updatedNotification;
      });

  service.init();

  return service;
}

const serviceArray = [
  '$http',
  '$q',
  '$translate',
  'taskTypesService',
  'timezoneService',
  'schemaService',
  'groupsService',
  'Publication',
  'siteService',
  'platformService',
  'companyService',
  notificationService
];

module.exports = (ngModule) => {
  ngModule.factory('notificationService', serviceArray);
};
